import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import * as d3 from 'd3'
import { TIERS, TIER_POINTS, parseDay, rosterAt } from './data'
import { useElementWidth } from './useElementSize'
import { Avatar, TierChip, formatDay, formatLongDay } from './ui'

const GROUPS = [...TIERS, null]

export function RosterPanel({ snapshots, loading, date, trackedSince }) {
  const current = rosterAt(snapshots, date)
  const previous = useMemo(() => {
    if (!current) return null
    const i = snapshots.indexOf(current)
    return i > 0 ? snapshots[i - 1] : null
  }, [snapshots, current])

  const diff = useMemo(() => {
    if (!current || !previous) return { joined: new Set(), from: new Map(), left: [] }
    const prevById = new Map(previous.roster.map(p => [p.player_id, p]))
    const currIds = new Set(current.roster.map(p => p.player_id))
    const joined = new Set()
    const from = new Map()
    for (const p of current.roster) {
      const before = prevById.get(p.player_id)
      if (!before) joined.add(p.player_id)
      else if (before.tier !== p.tier) from.set(p.player_id, before.tier)
    }
    return { joined, from, left: previous.roster.filter(p => !currIds.has(p.player_id)) }
  }, [current, previous])

  let body
  if (loading && !snapshots) body = <p className="hv-empty">Loading roster…</p>
  else if (!current) {
    body = (
      <p className="hv-empty">
        {trackedSince
          ? `Rosters are stored from ${formatLongDay(trackedSince)} onwards. Pick a later date to see who was on the team.`
          : 'Rosters will appear here after the next update, when roster tracking starts.'}
      </p>
    )
  } else {
    const ranked = current.roster.filter(p => p.tier)
    const avg = ranked.length ? ranked.reduce((s, p) => s + TIER_POINTS[p.tier], 0) / ranked.length : 0
    body = (
      <>
        <div className="hv-roster-summary">
          <span><b>{current.roster.length}</b> players</span>
          <span><b>{ranked.length}</b> ranked</span>
          <span>avg <b>{avg.toFixed(2)}</b> pts</span>
        </div>
        <div className="hv-roster">
          {GROUPS.map(tier => {
            const players = current.roster.filter(p => p.tier === tier)
            if (!players.length) return null
            return (
              <div key={tier || 'none'} className="hv-roster-row">
                <div className="hv-roster-tier">
                  <TierChip tier={tier} />
                  <span className="hv-muted hv-small">{tier ? `${TIER_POINTS[tier] > 0 ? '+' : ''}${TIER_POINTS[tier]}` : 'n/r'}</span>
                </div>
                <div className="hv-roster-players">
                  <AnimatePresence initial={false}>
                    {players.map(p => {
                      const isNew = diff.joined.has(p.player_id)
                      const fromTier = diff.from.get(p.player_id)
                      return (
                        <motion.div
                          key={p.player_id}
                          layout
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          className={`hv-player ${isNew ? 'is-new' : ''} ${fromTier !== undefined ? 'is-moved' : ''}`}
                          title={p.name}
                        >
                          <Avatar src={p.image} name={p.name} size={40} />
                          <span className="hv-player-name">{p.name}</span>
                          {isNew && <span className="hv-player-tag">New</span>}
                          {fromTier !== undefined && <span className="hv-player-tag">was {fromTier || 'n/r'}</span>}
                        </motion.div>
                      )
                    })}
                  </AnimatePresence>
                </div>
              </div>
            )
          })}
        </div>
        {diff.left.length > 0 && (
          <div className="hv-roster-left">
            <span className="hv-muted hv-small">Left since {formatDay(previous.date)}</span>
            <div className="hv-roster-players">
              {diff.left.map(p => (
                <div key={p.player_id} className="hv-player is-gone" title={p.name}>
                  <Avatar src={p.image} name={p.name} size={32} />
                  <span className="hv-player-name">{p.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <section className="hv-card hv-roster-card">
      <header className="hv-card-head">
        <div>
          <h3>Roster</h3>
          <p className="hv-muted hv-small">{current ? `As of ${formatLongDay(current.date)}` : 'Time-travel through the roster'}</p>
        </div>
      </header>
      {body}
    </section>
  )
}

const MIX_H = 150
const MIX_MARGIN = { top: 8, right: 12, bottom: 24, left: 28 }
const MIX_KEYS = ['none', ...[...TIERS].reverse()] // bottom → top: unranked, F … S

export function TierMix({ snapshots, dates, pinnedDate, onPinDate }) {
  const [ref, width] = useElementWidth()
  const [hoverIdx, setHoverIdx] = useState(null)
  const innerW = Math.max(0, width - MIX_MARGIN.left - MIX_MARGIN.right)
  const innerH = MIX_H - MIX_MARGIN.top - MIX_MARGIN.bottom

  const model = useMemo(() => {
    if (!snapshots?.length || !dates.length) return null
    const from = dates[0]
    const to = dates.at(-1)
    const inRange = snapshots.filter(s => s.date >= from && s.date <= to)
    // Carry in the snapshot active at the start of the range, if any.
    const startSnap = rosterAt(snapshots, from)
    const rows = (startSnap && startSnap.date < from ? [{ ...startSnap, date: from }, ...inRange] : inRange).map(s => {
      const counts = { date: s.date, none: 0, S: 0, A: 0, B: 0, C: 0, F: 0 }
      for (const p of s.roster) counts[p.tier || 'none']++
      return counts
    })
    if (rows.length && rows.at(-1).date < to) rows.push({ ...rows.at(-1), date: to })
    if (!rows.length) return null
    const stack = d3.stack().keys(MIX_KEYS)(rows)
    const x = d3.scaleUtc().domain([parseDay(from), parseDay(to)]).range([0, innerW])
    const y = d3.scaleLinear().domain([0, d3.max(rows, r => MIX_KEYS.reduce((s, k) => s + r[k], 0))]).nice().range([innerH, 0])
    const area = d3.area().x(d => x(parseDay(d.data.date))).y0(d => y(d[0])).y1(d => y(d[1])).curve(d3.curveStepAfter)
    return { rows, stack, x, y, area }
  }, [snapshots, dates, innerW, innerH])

  const xs = model ? model.rows.map(r => model.x(parseDay(r.date))) : []
  const hoverRow = hoverIdx != null && model ? model.rows[hoverIdx] : null

  return (
    <section className="hv-card hv-mix">
      <header className="hv-card-head">
        <div>
          <h3>Tier mix</h3>
          <p className="hv-muted hv-small">How many players of each tier were on the roster</p>
        </div>
        <ul className="hv-legend">
          {[...TIERS, null].map(t => (
            <li key={t || 'none'}>
              <TierChip tier={t} size="sm" /> {hoverRow ? hoverRow[t || 'none'] : ''}
            </li>
          ))}
        </ul>
      </header>
      <div className="hv-chart" ref={ref} style={{ height: MIX_H }}>
        {width > 0 && model && (
          <svg width={width} height={MIX_H} role="img" aria-label="Roster tier mix over time">
            <g transform={`translate(${MIX_MARGIN.left},${MIX_MARGIN.top})`}>
              {model.y.ticks(3).map(t => (
                <g key={t} transform={`translate(0,${model.y(t)})`}>
                  <line className="hv-grid" x2={innerW} />
                  <text className="hv-axis-label" x={-8} dy="0.32em" textAnchor="end">{t}</text>
                </g>
              ))}
              {model.stack.map(layer => (
                <path key={layer.key} className={`hv-mix-area hv-mix-area--${layer.key.toLowerCase()}`} d={model.area(layer)} />
              ))}
              {model.x.ticks(Math.max(2, Math.floor(innerW / 110))).map(t => (
                <text key={+t} className="hv-axis-label" x={model.x(t)} y={innerH + 18} textAnchor="middle">
                  {d3.utcFormat('%b %-d')(t)}
                </text>
              ))}
              {pinnedDate && pinnedDate >= dates[0] && (
                <line className="hv-pin-line" x1={model.x(parseDay(pinnedDate))} x2={model.x(parseDay(pinnedDate))} y2={innerH} />
              )}
              {hoverIdx != null && <line className="hv-hover-line" x1={xs[hoverIdx]} x2={xs[hoverIdx]} y2={innerH} />}
              <rect
                className="hv-hit"
                width={innerW}
                height={innerH}
                onPointerMove={e => {
                  const px = e.clientX - e.currentTarget.getBoundingClientRect().left
                  // Step chart: the active row is the last one at or before the pointer.
                  setHoverIdx(Math.max(0, d3.bisectRight(xs, px) - 1))
                }}
                onPointerLeave={() => setHoverIdx(null)}
                onClick={() => hoverRow && onPinDate(hoverRow.date)}
              />
            </g>
          </svg>
        )}
        {width > 0 && !model && <p className="hv-empty">No roster snapshots in this period yet.</p>}
      </div>
    </section>
  )
}
